
import { CommonModule } from '@angular/common';
import {
  HttpClient,
  HttpErrorResponse
} from '@angular/common/http';
import {
  ChangeDetectorRef,
  Component,
  OnInit
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { environment } from '../../../../environments/environment';
import {
  finalize,
  timeout
} from 'rxjs';
import Swal from 'sweetalert2';

interface MesDisponibilidade {
  mes: number;
  nome: string;
  liberado: boolean;
  salvando: boolean;
}

interface DisponibilidadeResponse {
  id: number;
  quartoId: number;
  ano: number;
  mes: number;
  liberado: boolean;
}

interface DisponibilidadeRequest {
  quartoId: number;
  ano: number;
  mes: number;
  liberado: boolean;
}

@Component({
  selector: 'app-disponibilidade-meses',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule
  ],
  templateUrl: './disponibilidade-meses.component.html',
  styleUrl: './disponibilidade-meses.component.css'
})
export class DisponibilidadeMesesComponent implements OnInit {

  private readonly apiUrl =
    `${environment.apiUrl}/disponibilidade-meses`;

  acomodacoes = [
    { id: 1, nome: 'Cabana Ypê' },
    { id: 2, nome: 'Chalé Santorini' }
  ];

  quartoId = 1;
  ano = new Date().getFullYear();

  anos: number[] = [];

  meses: MesDisponibilidade[] = [
    { mes: 1, nome: 'Janeiro', liberado: false, salvando: false },
    { mes: 2, nome: 'Fevereiro', liberado: false, salvando: false },
    { mes: 3, nome: 'Março', liberado: false, salvando: false },
    { mes: 4, nome: 'Abril', liberado: false, salvando: false },
    { mes: 5, nome: 'Maio', liberado: false, salvando: false },
    { mes: 6, nome: 'Junho', liberado: false, salvando: false },
    { mes: 7, nome: 'Julho', liberado: false, salvando: false },
    { mes: 8, nome: 'Agosto', liberado: false, salvando: false },
    { mes: 9, nome: 'Setembro', liberado: false, salvando: false },
    { mes: 10, nome: 'Outubro', liberado: false, salvando: false },
    { mes: 11, nome: 'Novembro', liberado: false, salvando: false },
    { mes: 12, nome: 'Dezembro', liberado: false, salvando: false }
  ];

  carregando = false;

  constructor(
    private readonly http: HttpClient,
    private readonly cdr: ChangeDetectorRef
  ) {
    const anoAtual = new Date().getFullYear();

    this.anos = Array.from(
      { length: 6 },
      (_, index) => anoAtual + index
    );
  }

  ngOnInit(): void {
    this.carregarDisponibilidade();
  }

  get totalLiberados(): number {
    return this.meses.filter(mes => mes.liberado).length;
  }

  get totalBloqueados(): number {
    return this.meses.length - this.totalLiberados;
  }

  carregarDisponibilidade(): void {
    this.carregando = true;

    this.meses.forEach(mes => {
      mes.liberado = false;
      mes.salvando = false;
    });

    this.cdr.detectChanges();

    this.http.get<DisponibilidadeResponse[]>(
      this.apiUrl,
      {
        params: {
          quartoId: this.quartoId,
          ano: this.ano
        }
      }
    ).pipe(
      timeout(15000),
      finalize(() => {
        this.carregando = false;
        this.cdr.detectChanges();
      })
    ).subscribe({
      next: (resposta) => {
        resposta.forEach(registro => {
          const mes = this.meses.find(
            item => item.mes === registro.mes
          );

          if (mes) {
            mes.liberado = registro.liberado;
          }
        });

        this.cdr.detectChanges();
      },

      error: (erro: unknown) => {
        const erroHttp = erro instanceof HttpErrorResponse
          ? erro
          : new HttpErrorResponse({
            error: erro,
            status: 0,
            statusText: 'Erro inesperado'
          });

        const mensagem =
          erro instanceof Error && erro.name === 'TimeoutError'
            ? 'O servidor demorou mais de 15 segundos para responder. Tente novamente.'
            : this.mensagemDoErro(
              erroHttp,
              'Não foi possível carregar os meses de disponibilidade.'
            );

        this.cdr.detectChanges();

        void Swal.fire({
          icon: 'error',
          title: 'Erro ao carregar disponibilidade',
          text: mensagem,
          confirmButtonText: 'Entendi',
          confirmButtonColor: '#696A49'
        });
      }
    });
  }

  alterarAcomodacao(): void {
    this.carregarDisponibilidade();
  }

  alterarAno(): void {
    this.carregarDisponibilidade();
  }

  alternarMes(mes: MesDisponibilidade): void {
    if (this.carregando || mes.salvando) {
      return;
    }

    const estadoAnterior = mes.liberado;
    const novoEstado = !estadoAnterior;

    mes.salvando = true;

    const payload: DisponibilidadeRequest = {
      quartoId: this.quartoId,
      ano: this.ano,
      mes: mes.mes,
      liberado: novoEstado
    };

    this.cdr.detectChanges();

    this.http.put<DisponibilidadeResponse>(
      this.apiUrl,
      payload
    ).pipe(
      timeout(15000),
      finalize(() => {
        mes.salvando = false;
        this.cdr.detectChanges();
      })
    ).subscribe({
      next: (resposta) => {
        mes.liberado = resposta.liberado;

        this.cdr.detectChanges();

        void Swal.fire({
          icon: 'success',
          title: resposta.liberado
            ? 'Mês liberado!'
            : 'Mês bloqueado!',
          text: resposta.liberado
            ? `${mes.nome} de ${this.ano} está liberado para novas reservas.`
            : `${mes.nome} de ${this.ano} está bloqueado para novas reservas.`,
          confirmButtonText: 'OK',
          confirmButtonColor: '#696A49',
          timer: 2500,
          timerProgressBar: true
        });
      },

      error: (erro: unknown) => {
        const erroHttp = erro instanceof HttpErrorResponse
          ? erro
          : new HttpErrorResponse({
            error: erro,
            status: 0,
            statusText: 'Erro inesperado'
          });

        const mensagem =
          erro instanceof Error && erro.name === 'TimeoutError'
            ? 'O servidor demorou mais de 15 segundos para responder. Tente novamente.'
            : this.mensagemDoErro(
              erroHttp,
              'Não foi possível carregar os meses de disponibilidade.'
            );

        this.cdr.detectChanges();

        void Swal.fire({
          icon: 'error',
          title: 'Erro ao carregar disponibilidade',
          text: mensagem,
          confirmButtonText: 'Entendi',
          confirmButtonColor: '#696A49'
        });
      }
    });
  }

  private mensagemDoErro(
    erro: HttpErrorResponse,
    mensagemPadrao: string
  ): string {
    if (erro.status === 401) {
      return 'Sua sessão expirou. Entre novamente no painel administrativo.';
    }

    if (erro.status === 403) {
      return 'Você não tem permissão de administrador para alterar a disponibilidade.';
    }

    if (erro.status === 0) {
      return 'Não foi possível conectar ao servidor. Verifique sua conexão e se o backend está ativo.';
    }

    return erro.error?.detail
      || erro.error?.message
      || erro.error?.mensagem
      || mensagemPadrao;
  }
}
